import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import Layout from "../components/Layout";
import { Globe, Plus, Copy, Check, Code2, Settings, Trash2, ChevronDown, ChevronUp, Bot } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SiteCard({ site, onEdit, onDelete, onSelect }) {
  const { t } = useLang();
  const [embedCode, setEmbedCode] = useState(null);
  const [showCode, setShowCode] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchEmbed = async () => {
    if (embedCode) { setShowCode(!showCode); return; }
    try {
      const { data } = await axios.get(`${API}/sites/${site.id}/embed-code`);
      setEmbedCode(data.embed_code);
      setShowCode(true);
    } catch {}
  };

  const copyCode = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-100 p-5 card-hover animate-fade-in-up" data-testid={`site-card-${site.id}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-lg">
            {site.name[0].toUpperCase()}
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">{site.name}</h3>
            <p className="text-sm text-slate-500">{site.domain}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${site.is_active ? "bg-emerald-500" : "bg-slate-300"}`} />
          <span className="text-xs text-slate-500">{site.is_active ? "Aktif" : "Pasif"}</span>
          {site.widget_config?.widget_hidden && (
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Widget Gizli</span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => onSelect(site)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-lg hover:bg-blue-100 transition-colors"
          data-testid={`site-settings-${site.id}`}
        >
          <Settings size={13} /> {t("sites.widget_settings")}
        </button>
        <button
          onClick={fetchEmbed}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-100 transition-colors"
          data-testid={`site-embed-${site.id}`}
        >
          <Code2 size={13} /> {t("sites.embed_code")}
          {showCode ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
        <button
          onClick={() => onDelete(site.id)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 text-xs font-medium rounded-lg hover:bg-red-100 transition-colors ml-auto"
          data-testid={`site-delete-${site.id}`}
        >
          <Trash2 size={13} />
        </button>
      </div>

      {showCode && embedCode && (
        <div className="mt-4 relative animate-fade-in-up">
          <pre className="bg-slate-900 text-slate-200 text-xs p-4 rounded-xl overflow-x-auto font-mono leading-relaxed">
            {embedCode}
          </pre>
          <button
            onClick={copyCode}
            className="absolute top-2 right-2 flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs rounded-lg transition-colors"
            data-testid={`copy-embed-${site.id}`}
          >
            {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
            {copied ? t("sites.copied") : t("sites.copy_code")}
          </button>
        </div>
      )}

      {site.ai_config?.enabled && (
        <div className="mt-3 flex items-center gap-1.5 text-emerald-600 text-xs">
          <Bot size={13} /> AI Chatbot aktif
        </div>
      )}
    </div>
  );
}

function AddSiteModal({ onClose, onCreated }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", domain: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/sites`, form);
      onCreated(data);
    } catch (err) {
      alert(err?.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl animate-fade-in-up">
        <h2 className="font-heading text-xl font-semibold text-slate-900 mb-5">{t("sites.add_site")}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("sites.name")}</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:outline-none focus:border-blue-400"
              placeholder="Örn: Mağazam"
              required
              data-testid="site-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("sites.domain")}</label>
            <input
              value={form.domain}
              onChange={(e) => setForm({ ...form, domain: e.target.value })}
              className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:outline-none focus:border-blue-400"
              placeholder="https://magaza.com"
              required
              data-testid="site-domain-input"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-11 rounded-xl border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 h-11 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60 shadow-lg shadow-blue-500/25"
              data-testid="create-site-btn"
            >
              {loading ? t("common.loading") : t("sites.add_site")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SiteSettingsModal({ site, onClose, onUpdated }) {
  const { t } = useLang();
  const [widgetConfig, setWidgetConfig] = useState(site.widget_config || {});
  const [aiConfig, setAiConfig] = useState(site.ai_config || {});
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("widget");

  const defaultSchedule = {
    mon: { active: true,  open: "09:00", close: "18:00" },
    tue: { active: true,  open: "09:00", close: "18:00" },
    wed: { active: true,  open: "09:00", close: "18:00" },
    thu: { active: true,  open: "09:00", close: "18:00" },
    fri: { active: true,  open: "09:00", close: "18:00" },
    sat: { active: false, open: "10:00", close: "15:00" },
    sun: { active: false, open: "00:00", close: "00:00" },
  };
  const DAY_LABELS = { mon:"Pzt", tue:"Sal", wed:"Çar", thu:"Per", fri:"Cum", sat:"Cmt", sun:"Paz" };

  const wh = widgetConfig.working_hours || {};
  const schedule = wh.schedule || defaultSchedule;

  const setWH = (patch) => setWidgetConfig(prev => ({
    ...prev,
    working_hours: { ...(prev.working_hours || {}), ...patch }
  }));
  const setDay = (day, patch) => setWH({
    schedule: { ...schedule, [day]: { ...(schedule[day] || defaultSchedule[day]), ...patch } }
  });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/sites/${site.id}`, { widget_config: widgetConfig, ai_config: aiConfig });
      onUpdated(data);
    } catch {}
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl animate-fade-in-up max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-slate-100">
          <h2 className="font-heading text-xl font-semibold text-slate-900">{site.name} — Ayarlar</h2>
          <div className="flex gap-2 mt-4 overflow-x-auto">
            {[["widget","Widget"], ["hours","Mesai Saatleri"], ["ai","AI Ayarları"]].map(([key,label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${tab === key ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                data-testid={`tab-${key}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {tab === "widget" && (
            <div className="space-y-4">

              {/* Widget Görünürlük Toggle */}
              <div className={`flex items-center justify-between p-4 rounded-xl border-2 transition-colors ${widgetConfig.widget_hidden ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${widgetConfig.widget_hidden ? "bg-amber-400" : "bg-emerald-500 animate-pulse"}`} />
                    <span className="text-sm font-semibold text-slate-800">
                      {widgetConfig.widget_hidden ? "Widget Gizli" : "Widget Görünür"}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 ml-4">
                    {widgetConfig.widget_hidden
                      ? "Ziyaretçiler widget'ı görmez — analitik takip devam eder"
                      : "Widget aktif, ziyaretçiler görebilir ve mesaj gönderebilir"}
                  </p>
                </div>
                <button
                  onClick={() => setWidgetConfig({ ...widgetConfig, widget_hidden: !widgetConfig.widget_hidden })}
                  className={`w-12 h-6 rounded-full transition-colors relative flex-shrink-0 ${widgetConfig.widget_hidden ? "bg-amber-400" : "bg-emerald-500"}`}
                  data-testid="widget-hidden-toggle"
                >
                  <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform shadow-sm ${widgetConfig.widget_hidden ? "translate-x-6" : "translate-x-0.5"}`} />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("sites.primary_color")}</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={widgetConfig.primary_color || "#4f46e5"}
                    onChange={(e) => setWidgetConfig({ ...widgetConfig, primary_color: e.target.value })}
                    className="w-12 h-10 rounded-lg cursor-pointer border border-slate-200"
                    data-testid="color-picker"
                  />
                  <span className="text-sm text-slate-600 font-mono">{widgetConfig.primary_color || "#4f46e5"}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("sites.position")}</label>
                <div className="flex gap-3">
                  {["right", "left"].map((pos) => (
                    <button
                      key={pos}
                      onClick={() => setWidgetConfig({ ...widgetConfig, position: pos })}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${widgetConfig.position === pos || (!widgetConfig.position && pos === "right") ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                    >
                      {pos === "right" ? t("sites.right") : t("sites.left")}
                    </button>
                  ))}
                </div>
              </div>

              {/* Bottom offset */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Alt Boşluk (bottom) &nbsp;
                  <span className="font-bold text-blue-600">{widgetConfig.bottom_offset ?? 24}px</span>
                </label>
                <input
                  type="range" min={0} max={120} step={4}
                  value={widgetConfig.bottom_offset ?? 24}
                  onChange={(e) => setWidgetConfig({ ...widgetConfig, bottom_offset: parseInt(e.target.value) })}
                  className="w-full accent-blue-600"
                  data-testid="bottom-offset-slider"
                />
                <div className="flex justify-between text-xs text-slate-400 mt-1">
                  <span>0px</span>
                  <span>Varsayılan: 24px</span>
                  <span>120px</span>
                </div>
              </div>
              {/* Karşılama Ekranı */}
              <div className="border-t border-slate-100 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Karşılama Ekranı</p>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input type="checkbox" checked={widgetConfig.home_enabled !== false}
                      onChange={(e) => setWidgetConfig({ ...widgetConfig, home_enabled: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400" data-testid="home-enabled-toggle" />
                    <span className="text-xs text-slate-600">Göster</span>
                  </label>
                </div>
                {widgetConfig.home_enabled !== false && (
                  <>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Başlık <span className="text-xs font-normal text-slate-400">(vurgu için **kelime**)</span></label>
                    <input value={widgetConfig.home_title || ""} onChange={(e) => setWidgetConfig({ ...widgetConfig, home_title: e.target.value })}
                      className="w-full h-10 rounded-xl border border-slate-200 px-4 text-sm focus:outline-none focus:border-blue-400" placeholder="Size nasıl **yardımcı** olabiliriz?" data-testid="home-title-input" />
                    <input value={widgetConfig.home_title_en || ""} onChange={(e) => setWidgetConfig({ ...widgetConfig, home_title_en: e.target.value })}
                      className="w-full h-9 mt-1.5 rounded-lg border border-slate-200 px-3 text-sm text-slate-600 focus:outline-none focus:border-blue-400" placeholder="EN: How can we **help** you?" />

                    <label className="block text-sm font-medium text-slate-700 mb-1.5 mt-3">Alt metin</label>
                    <textarea value={widgetConfig.home_subtitle || ""} onChange={(e) => setWidgetConfig({ ...widgetConfig, home_subtitle: e.target.value })} rows={2}
                      className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm resize-none focus:outline-none focus:border-blue-400" placeholder="Ekibimize buradan yazabilirsiniz. Sorularınızı en kısa sürede yanıtlıyoruz." />
                    <input value={widgetConfig.home_subtitle_en || ""} onChange={(e) => setWidgetConfig({ ...widgetConfig, home_subtitle_en: e.target.value })}
                      className="w-full h-9 mt-1.5 rounded-lg border border-slate-200 px-3 text-sm text-slate-600 focus:outline-none focus:border-blue-400" placeholder="EN: Message our team right here." />

                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Buton metni</label>
                        <input value={widgetConfig.start_button || ""} onChange={(e) => setWidgetConfig({ ...widgetConfig, start_button: e.target.value })}
                          className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:border-blue-400" placeholder="Sohbeti başlat" />
                        <input value={widgetConfig.start_button_en || ""} onChange={(e) => setWidgetConfig({ ...widgetConfig, start_button_en: e.target.value })}
                          className="w-full h-9 mt-1.5 rounded-lg border border-slate-200 px-3 text-sm text-slate-600 focus:outline-none focus:border-blue-400" placeholder="EN: Start chat" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Yanıt süresi</label>
                        <input value={widgetConfig.response_time || ""} onChange={(e) => setWidgetConfig({ ...widgetConfig, response_time: e.target.value })}
                          className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:border-blue-400" placeholder="Birkaç dakika" />
                        <input value={widgetConfig.response_time_en || ""} onChange={(e) => setWidgetConfig({ ...widgetConfig, response_time_en: e.target.value })}
                          className="w-full h-9 mt-1.5 rounded-lg border border-slate-200 px-3 text-sm text-slate-600 focus:outline-none focus:border-blue-400" placeholder="EN: A few minutes" />
                      </div>
                    </div>

                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-slate-700">Sık Sorulan Sorular</label>
                        <button type="button" onClick={() => setWidgetConfig({ ...widgetConfig, faq: [...(widgetConfig.faq || []), { q: "", a: "", q_en: "", a_en: "" }] })}
                          className="text-xs font-medium text-blue-600 hover:text-blue-700" data-testid="faq-add">+ Soru ekle</button>
                      </div>
                      {(widgetConfig.faq || []).map((f, i) => (
                        <div key={i} className="border border-slate-200 rounded-xl p-3 mb-2 bg-slate-50">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-xs font-semibold text-slate-400">#{i + 1}</span>
                            <button type="button" onClick={() => setWidgetConfig({ ...widgetConfig, faq: (widgetConfig.faq || []).filter((_, j) => j !== i) })}
                              className="text-xs text-red-500 hover:text-red-600">Sil</button>
                          </div>
                          <input value={f.q || ""} onChange={(e) => { const n = [...(widgetConfig.faq || [])]; n[i] = { ...n[i], q: e.target.value }; setWidgetConfig({ ...widgetConfig, faq: n }); }}
                            className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm mb-1.5 focus:outline-none focus:border-blue-400" placeholder="Soru (TR)" />
                          <textarea value={f.a || ""} onChange={(e) => { const n = [...(widgetConfig.faq || [])]; n[i] = { ...n[i], a: e.target.value }; setWidgetConfig({ ...widgetConfig, faq: n }); }} rows={2}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm resize-none mb-1.5 focus:outline-none focus:border-blue-400" placeholder="Cevap (TR)" />
                          <input value={f.q_en || ""} onChange={(e) => { const n = [...(widgetConfig.faq || [])]; n[i] = { ...n[i], q_en: e.target.value }; setWidgetConfig({ ...widgetConfig, faq: n }); }}
                            className="w-full h-8 rounded-lg border border-slate-200 px-3 text-sm text-slate-600 mb-1.5 focus:outline-none focus:border-blue-400" placeholder="EN: Question" />
                          <input value={f.a_en || ""} onChange={(e) => { const n = [...(widgetConfig.faq || [])]; n[i] = { ...n[i], a_en: e.target.value }; setWidgetConfig({ ...widgetConfig, faq: n }); }}
                            className="w-full h-8 rounded-lg border border-slate-200 px-3 text-sm text-slate-600 focus:outline-none focus:border-blue-400" placeholder="EN: Answer" />
                        </div>
                      ))}
                      {(!widgetConfig.faq || widgetConfig.faq.length === 0) && (
                        <p className="text-xs text-slate-400">Henüz soru yok. "+ Soru ekle" ile ekleyin (boş bırakılırsa SSS bölümü gizli kalır).</p>
                      )}
                    </div>

                    {/* Temsilci avatarları */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Temsilci avatarları <span className="text-xs font-normal text-slate-400">(ekip hissi; 1-3 görsel URL)</span></label>
                      {[0, 1, 2].map((i) => (
                        <input key={i} value={(widgetConfig.home_avatars || [])[i] || ""}
                          onChange={(e) => { const n = [...(widgetConfig.home_avatars || [])]; while (n.length < 3) n.push(""); n[i] = e.target.value; setWidgetConfig({ ...widgetConfig, home_avatars: n }); }}
                          className="w-full h-9 mb-1.5 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:border-blue-400" placeholder={`Avatar ${i + 1} URL (boşsa bot avatarı)`} />
                      ))}
                    </div>

                    {/* Sizi Arayalım */}
                    <div className="mt-4">
                      <label className="flex items-center gap-2.5 cursor-pointer select-none mb-2">
                        <input type="checkbox" checked={widgetConfig.callback_enabled === true}
                          onChange={(e) => setWidgetConfig({ ...widgetConfig, callback_enabled: e.target.checked })}
                          className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400" data-testid="callback-enabled-toggle" />
                        <span className="text-sm text-slate-700">"Sizi Arayalım" geri-arama butonu</span>
                      </label>
                      {widgetConfig.callback_enabled && (
                        <>
                          <input value={widgetConfig.callback_button || ""} onChange={(e) => setWidgetConfig({ ...widgetConfig, callback_button: e.target.value })}
                            className="w-full h-10 rounded-xl border border-slate-200 px-4 text-sm focus:outline-none focus:border-blue-400" placeholder="Sizi Arayalım" />
                          <input value={widgetConfig.callback_button_en || ""} onChange={(e) => setWidgetConfig({ ...widgetConfig, callback_button_en: e.target.value })}
                            className="w-full h-9 mt-1.5 rounded-lg border border-slate-200 px-3 text-sm text-slate-600 focus:outline-none focus:border-blue-400" placeholder="EN: Call me back" />
                          <p className="text-xs text-slate-400 mt-1">Ziyaretçi ad + telefon bırakır → inbox'ta "pending" konuşma + admin'e e-posta gider.</p>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("sites.greeting")}</label>
                <textarea
                  value={widgetConfig.greeting || ""}
                  onChange={(e) => setWidgetConfig({ ...widgetConfig, greeting: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-blue-400 resize-none"
                  rows={3}
                  placeholder="Merhaba! Size nasıl yardımcı olabiliriz?"
                  data-testid="greeting-input"
                />
                <input
                  value={widgetConfig.greeting_en || ""}
                  onChange={(e) => setWidgetConfig({ ...widgetConfig, greeting_en: e.target.value })}
                  className="w-full h-9 mt-1.5 rounded-lg border border-slate-200 px-3 text-sm text-slate-600 focus:outline-none focus:border-blue-400"
                  placeholder="EN: Hi! How can we help you?"
                  data-testid="greeting-en-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Footer Metni</label>
                <input
                  value={widgetConfig.footer_text || ""}
                  onChange={(e) => setWidgetConfig({ ...widgetConfig, footer_text: e.target.value })}
                  className="w-full h-10 rounded-xl border border-slate-200 px-4 text-sm focus:outline-none focus:border-blue-400"
                  placeholder={`${site.name} • AI Destekli`}
                  data-testid="footer-text-input"
                />
                <input
                  value={widgetConfig.footer_text_en || ""}
                  onChange={(e) => setWidgetConfig({ ...widgetConfig, footer_text_en: e.target.value })}
                  className="w-full h-9 mt-1.5 rounded-lg border border-slate-200 px-3 text-sm text-slate-600 focus:outline-none focus:border-blue-400"
                  placeholder={`EN: ${site.name} • AI Powered`}
                  data-testid="footer-text-en-input"
                />
                <p className="text-xs text-slate-400 mt-1">Widget alt kısmında görünen metin. Boş bırakılırsa site adı kullanılır.</p>
              </div>

              {/* Operatör görünen adı */}
              <div className="border-t border-slate-100 pt-4">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Operatör görünen adı</label>
                <input
                  value={widgetConfig.agent_label ?? "Müşteri Temsilcisi"}
                  onChange={(e) => setWidgetConfig({ ...widgetConfig, agent_label: e.target.value })}
                  className="w-full h-10 rounded-xl border border-slate-200 px-4 text-sm focus:outline-none focus:border-blue-400"
                  placeholder="Müşteri Temsilcisi"
                  data-testid="agent-label-input"
                />
                <input
                  value={widgetConfig.agent_label_en || ""}
                  onChange={(e) => setWidgetConfig({ ...widgetConfig, agent_label_en: e.target.value })}
                  className="w-full h-9 mt-1.5 rounded-lg border border-slate-200 px-3 text-sm text-slate-600 focus:outline-none focus:border-blue-400"
                  placeholder="EN: Support Agent"
                  data-testid="agent-label-en-input"
                />
                <p className="text-xs text-slate-400 mt-1">Ziyaretçiye personelin gerçek adı yerine bu yazı gösterilir (ör. Operatör, Destek Ekibi).</p>
              </div>

              {/* Bot Avatar */}
              <div className="border-t border-slate-100 pt-4">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Bot Avatar & Teaser</p>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Bot Avatar URL</label>
                <input
                  value={widgetConfig.bot_avatar || ""}
                  onChange={(e) => setWidgetConfig({ ...widgetConfig, bot_avatar: e.target.value })}
                  className="w-full h-10 rounded-xl border border-slate-200 px-4 text-sm focus:outline-none focus:border-blue-400"
                  placeholder="https://ornek.com/robot.png (boş bırakılırsa harf gösterilir)"
                  data-testid="bot-avatar-input"
                />
                {widgetConfig.bot_avatar && (
                  <div className="mt-2 flex items-center gap-3">
                    <img
                      src={widgetConfig.bot_avatar}
                      alt="Bot avatar"
                      className="w-12 h-12 rounded-full object-cover border border-slate-200 bg-slate-50"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <span className="text-xs text-slate-500">Önizleme</span>
                  </div>
                )}
              </div>

              {/* Teaser */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Teaser Başlığı</label>
                <input
                  value={widgetConfig.teaser_title || ""}
                  onChange={(e) => setWidgetConfig({ ...widgetConfig, teaser_title: e.target.value })}
                  className="w-full h-10 rounded-xl border border-slate-200 px-4 text-sm focus:outline-none focus:border-blue-400"
                  placeholder="Netlen AI'a Sor"
                  data-testid="teaser-title-input"
                />
                <input
                  value={widgetConfig.teaser_title_en || ""}
                  onChange={(e) => setWidgetConfig({ ...widgetConfig, teaser_title_en: e.target.value })}
                  className="w-full h-9 mt-1.5 rounded-lg border border-slate-200 px-3 text-sm text-slate-600 focus:outline-none focus:border-blue-400"
                  placeholder="EN: Ask us"
                  data-testid="teaser-title-en-input"
                />
                <p className="text-xs text-slate-400 mt-1">Widget açılmadan önce gösterilen popup başlığı.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Teaser Alt Metni</label>
                <input
                  value={widgetConfig.teaser_sub || ""}
                  onChange={(e) => setWidgetConfig({ ...widgetConfig, teaser_sub: e.target.value })}
                  className="w-full h-10 rounded-xl border border-slate-200 px-4 text-sm focus:outline-none focus:border-blue-400"
                  placeholder="Sorularınızı hemen yanıtlayabilirim"
                  data-testid="teaser-sub-input"
                />
                <input
                  value={widgetConfig.teaser_sub_en || ""}
                  onChange={(e) => setWidgetConfig({ ...widgetConfig, teaser_sub_en: e.target.value })}
                  className="w-full h-9 mt-1.5 rounded-lg border border-slate-200 px-3 text-sm text-slate-600 focus:outline-none focus:border-blue-400"
                  placeholder="EN: We're online"
                  data-testid="teaser-sub-en-input"
                />
              </div>
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={widgetConfig.teaser_hide_mobile === true}
                  onChange={(e) => setWidgetConfig({ ...widgetConfig, teaser_hide_mobile: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400"
                  data-testid="teaser-hide-mobile-checkbox"
                />
                <span className="text-sm text-slate-700">Mobil cihazlarda teaser baloncuğunu gizle</span>
              </label>

              {/* Gecikmeler */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Widget gecikmesi (sn)</label>
                  <input
                    type="number" min="0" step="1"
                    value={widgetConfig.widget_delay ?? 0}
                    onChange={(e) => setWidgetConfig({ ...widgetConfig, widget_delay: e.target.value === "" ? 0 : Math.max(0, parseFloat(e.target.value) || 0) })}
                    className="w-full h-10 rounded-xl border border-slate-200 px-4 text-sm focus:outline-none focus:border-blue-400"
                    placeholder="0"
                    data-testid="widget-delay-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">Buton bu kadar saniye sonra görünür (0 = hemen)</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Teaser gecikmesi (sn)</label>
                  <input
                    type="number" min="0" step="1"
                    value={widgetConfig.teaser_delay ?? 3}
                    onChange={(e) => setWidgetConfig({ ...widgetConfig, teaser_delay: e.target.value === "" ? 0 : Math.max(0, parseFloat(e.target.value) || 0) })}
                    className="w-full h-10 rounded-xl border border-slate-200 px-4 text-sm focus:outline-none focus:border-blue-400"
                    placeholder="3"
                    data-testid="teaser-delay-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">Baloncuk, widget göründükten bu kadar sonra çıkar</p>
                </div>
              </div>

              {/* Özel Buton Görseli */}
              <div className="border-t border-slate-100 pt-4">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Özel Buton Görseli</p>
                <p className="text-xs text-slate-500 mb-3">Sağ alttaki yuvarlak chat butonunun yerine özel görsel kullanın.</p>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Görsel URL</label>
                <input
                  value={widgetConfig.launch_image || ""}
                  onChange={(e) => setWidgetConfig({ ...widgetConfig, launch_image: e.target.value })}
                  className="w-full h-10 rounded-xl border border-slate-200 px-4 text-sm focus:outline-none focus:border-blue-400"
                  placeholder="https://ornek.com/ai-bot.png (boş bırakılırsa yuvarlak buton görünür)"
                  data-testid="launch-image-input"
                />
                {widgetConfig.launch_image && (
                  <div className="mt-2 flex items-center gap-3 p-3 bg-slate-900 rounded-xl">
                    <img
                      src={widgetConfig.launch_image}
                      alt="Buton görseli"
                      style={{ width: `${widgetConfig.launch_size || 80}px`, height: `${widgetConfig.launch_size || 80}px`, objectFit: "contain" }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <span className="text-xs text-slate-400">Önizleme ({widgetConfig.launch_size || 80}px)</span>
                  </div>
                )}
                <div className="mt-3">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Masaüstü boyut: <span className="font-bold text-blue-600">{widgetConfig.launch_size || 80}px</span>
                  </label>
                  <input
                    type="range"
                    min="40"
                    max="160"
                    step="4"
                    value={widgetConfig.launch_size || 80}
                    onChange={(e) => setWidgetConfig({ ...widgetConfig, launch_size: parseInt(e.target.value) })}
                    className="w-full accent-blue-600"
                    data-testid="launch-size-slider"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>Küçük (40px)</span><span>Büyük (160px)</span>
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Mobil boyut: <span className="font-bold text-blue-600">{widgetConfig.launch_size_mobile ?? widgetConfig.launch_size ?? 80}px</span>
                  </label>
                  <input
                    type="range"
                    min="40"
                    max="160"
                    step="4"
                    value={widgetConfig.launch_size_mobile ?? widgetConfig.launch_size ?? 80}
                    onChange={(e) => setWidgetConfig({ ...widgetConfig, launch_size_mobile: parseInt(e.target.value) })}
                    className="w-full accent-blue-600"
                    data-testid="launch-size-mobile-slider"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>Küçük (40px)</span><span>Büyük (160px)</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">≤768px genişlikte bu boyut kullanılır (boş bırakılırsa masaüstü ile aynı)</p>
                </div>
              </div>
            </div>
          )}

          {tab === "hours" && (
            <div className="space-y-5">
              {/* Enable toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div>
                  <div className="font-medium text-slate-900 text-sm">Mesai Saatleri Aktif</div>
                  <div className="text-xs text-slate-500 mt-0.5">Açık olduğunda mesai dışı saatlerde offline form gösterilir</div>
                </div>
                <button
                  onClick={() => setWH({ enabled: !wh.enabled })}
                  className={`w-12 h-6 rounded-full transition-colors relative ${wh.enabled ? "bg-blue-600" : "bg-slate-300"}`}
                  data-testid="hours-toggle"
                >
                  <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${wh.enabled ? "translate-x-6" : "translate-x-0.5"}`} />
                </button>
              </div>

              {wh.enabled && (
                <>
                  {/* Timezone */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Saat Dilimi</label>
                    <select
                      value={wh.timezone || "Europe/Istanbul"}
                      onChange={(e) => setWH({ timezone: e.target.value })}
                      className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:border-blue-400 bg-white"
                      data-testid="timezone-select"
                    >
                      <option value="Europe/Istanbul">Türkiye (UTC+3)</option>
                      <option value="Europe/London">Londra (UTC+0/+1)</option>
                      <option value="Europe/Berlin">Berlin (UTC+1/+2)</option>
                      <option value="America/New_York">New York (UTC-5/-4)</option>
                      <option value="Asia/Dubai">Dubai (UTC+4)</option>
                    </select>
                  </div>

                  {/* Day schedule */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Günlük Program</label>
                    <div className="space-y-2">
                      {Object.keys(defaultSchedule).map((day) => {
                        const d = schedule[day] || defaultSchedule[day];
                        return (
                          <div key={day} className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-50">
                            <button
                              onClick={() => setDay(day, { active: !d.active })}
                              className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${d.active ? "bg-blue-600" : "bg-slate-300"}`}
                              data-testid={`day-toggle-${day}`}
                            >
                              <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform ${d.active ? "translate-x-5" : "translate-x-0.5"}`} />
                            </button>
                            <span className="text-xs font-semibold text-slate-600 w-7 flex-shrink-0">{DAY_LABELS[day]}</span>
                            {d.active ? (
                              <div className="flex items-center gap-2 flex-1">
                                <input
                                  type="time"
                                  value={d.open}
                                  onChange={(e) => setDay(day, { open: e.target.value })}
                                  className="flex-1 h-8 rounded-lg border border-slate-200 px-2 text-xs focus:outline-none focus:border-blue-400 bg-white"
                                  data-testid={`open-${day}`}
                                />
                                <span className="text-slate-400 text-xs">—</span>
                                <input
                                  type="time"
                                  value={d.close}
                                  onChange={(e) => setDay(day, { close: e.target.value })}
                                  className="flex-1 h-8 rounded-lg border border-slate-200 px-2 text-xs focus:outline-none focus:border-blue-400 bg-white"
                                  data-testid={`close-${day}`}
                                />
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400 flex-1">Kapalı</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Mesai dışı AI modu */}
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
                    <div>
                      <div className="font-medium text-slate-900 text-sm">Mesai Dışında AI Aktif</div>
                      <div className="text-xs text-slate-500 mt-0.5">Açıksa offline form gösterilmez, AI yanıt verir. Temsilci butonu gizlenir.</div>
                    </div>
                    <button
                      onClick={() => setWH({ ai_in_offhours: !wh.ai_in_offhours })}
                      className={`w-12 h-6 rounded-full transition-colors relative flex-shrink-0 ${wh.ai_in_offhours ? "bg-blue-600" : "bg-slate-300"}`}
                      data-testid="ai-in-offhours-toggle"
                    >
                      <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${wh.ai_in_offhours ? "translate-x-6" : "translate-x-0.5"}`} />
                    </button>
                  </div>

                  {/* Offline messages */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Offline Başlık</label>
                    <input
                      value={wh.offline_title || ""}
                      onChange={(e) => setWH({ offline_title: e.target.value })}
                      className="w-full h-10 rounded-xl border border-slate-200 px-4 text-sm focus:outline-none focus:border-blue-400"
                      placeholder="Şu an çevrimdışıyız"
                      data-testid="offline-title-input"
                    />
                    <input
                      value={wh.offline_title_en || ""}
                      onChange={(e) => setWH({ offline_title_en: e.target.value })}
                      className="w-full h-9 mt-1.5 rounded-lg border border-slate-200 px-3 text-sm text-slate-600 focus:outline-none focus:border-blue-400"
                      placeholder="EN: We're currently offline"
                      data-testid="offline-title-en-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Offline Mesaj</label>
                    <textarea
                      value={wh.offline_message || ""}
                      onChange={(e) => setWH({ offline_message: e.target.value })}
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-blue-400 resize-none"
                      rows={3}
                      placeholder="Mesajınızı bırakın, mesai saatlerimizde size geri döneceğiz."
                      data-testid="offline-message-input"
                    />
                    <input
                      value={wh.offline_message_en || ""}
                      onChange={(e) => setWH({ offline_message_en: e.target.value })}
                      className="w-full h-9 mt-1.5 rounded-lg border border-slate-200 px-3 text-sm text-slate-600 focus:outline-none focus:border-blue-400"
                      placeholder="EN: Leave a message and we'll get back to you."
                      data-testid="offline-message-en-input"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "ai" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
                <div>
                  <div className="font-medium text-slate-900 text-sm">{t("sites.enable_ai")}</div>
                  <div className="text-xs text-slate-500 mt-0.5">AI chatbot otomatik yanıt verir</div>
                </div>
                <button
                  onClick={() => setAiConfig({ ...aiConfig, enabled: !aiConfig.enabled })}
                  className={`w-12 h-6 rounded-full transition-colors relative ${aiConfig.enabled ? "bg-blue-600" : "bg-slate-300"}`}
                  data-testid="ai-toggle"
                >
                  <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${aiConfig.enabled ? "translate-x-6" : "translate-x-0.5"}`} />
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("ai.model")}</label>
                <select
                  value={aiConfig.model || "gpt-4o"}
                  onChange={(e) => setAiConfig({ ...aiConfig, model: e.target.value })}
                  className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:outline-none focus:border-blue-400 bg-white"
                  data-testid="ai-model-select"
                >
                  <optgroup label="OpenAI">
                    <option value="gpt-4o">GPT-4o</option>
                    <option value="gpt-4o-mini">GPT-4o Mini</option>
                    <option value="gpt-5.1">GPT-5.1</option>
                  </optgroup>
                  <optgroup label="Claude (Anthropic)">
                    <option value="claude-haiku-5-5">Claude Haiku 5.5 (Hızlı)</option>
                    <option value="claude-sonnet-5-5">Claude Sonnet 5.5 (Dengeli)</option>
                    <option value="claude-opus-5-5">Claude Opus 5.5 (En Güçlü)</option>
                  </optgroup>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t("ai.temperature")}: {aiConfig.temperature || 0.7}
                </label>
                <input
                  type="range" min="0" max="1" step="0.1"
                  value={aiConfig.temperature || 0.7}
                  onChange={(e) => setAiConfig({ ...aiConfig, temperature: parseFloat(e.target.value) })}
                  className="w-full accent-blue-600"
                  data-testid="temperature-slider"
                />
                <div className="flex justify-between text-xs text-slate-400 mt-1">
                  <span>Kesin (0)</span><span>Yaratıcı (1)</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("ai.system_prompt")}</label>
                <textarea
                  value={aiConfig.system_prompt || ""}
                  onChange={(e) => setAiConfig({ ...aiConfig, system_prompt: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-blue-400 resize-none"
                  rows={4}
                  placeholder="Sen yardımsever bir müşteri destek asistanısın..."
                  data-testid="system-prompt-input"
                />
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-100 flex gap-3">
          <button onClick={onClose} className="flex-1 h-11 border border-slate-200 rounded-xl text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors">
            {t("common.cancel")}
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="flex-1 h-11 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60 shadow-lg shadow-blue-500/25"
            data-testid="save-site-btn"
          >
            {saving ? t("common.loading") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Sites() {
  const { t } = useLang();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingSite, setEditingSite] = useState(null);

  useEffect(() => {
    axios.get(`${API}/sites`).then(({ data }) => setSites(data)).finally(() => setLoading(false));
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Bu siteyi silmek istediğinizden emin misiniz?")) return;
    await axios.delete(`${API}/sites/${id}`);
    setSites((prev) => prev.filter((s) => s.id !== id));
  };

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8 animate-fade-in-up">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900">{t("sites.title")}</h1>
            <p className="text-slate-500 mt-1">Web sitelerinizi yönetin ve embed kodu alın</p>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-full font-semibold text-sm hover:bg-blue-700 transition-colors shadow-lg shadow-blue-500/25"
            data-testid="add-site-btn"
          >
            <Plus size={16} /> {t("sites.add_site")}
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-400">{t("common.loading")}</div>
        ) : sites.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-slate-100">
            <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
              <Globe size={28} className="text-blue-400" />
            </div>
            <h3 className="font-heading font-semibold text-slate-700 mb-2">{t("sites.no_sites")}</h3>
            <p className="text-slate-500 text-sm mb-5">{t("sites.add_first")}</p>
            <button
              onClick={() => setShowAdd(true)}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-full font-semibold text-sm hover:bg-blue-700 transition-colors"
              data-testid="add-site-empty-btn"
            >
              {t("sites.add_site")}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 stagger-children">
            {sites.map((site) => (
              <SiteCard
                key={site.id}
                site={site}
                onEdit={() => setEditingSite(site)}
                onSelect={(s) => setEditingSite(s)}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {showAdd && (
        <AddSiteModal
          onClose={() => setShowAdd(false)}
          onCreated={(s) => { setSites((prev) => [s, ...prev]); setShowAdd(false); }}
        />
      )}

      {editingSite && (
        <SiteSettingsModal
          site={editingSite}
          onClose={() => setEditingSite(null)}
          onUpdated={(updated) => {
            setSites((prev) => prev.map((s) => s.id === updated.id ? updated : s));
            setEditingSite(null);
          }}
        />
      )}
    </Layout>
  );
}
